"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import type { AutoplayConfig } from "./types";

// Extract type from embla-carousel-react to avoid installing base package
type EmblaCarouselType = ReturnType<typeof useEmblaCarousel>[1];

const DEFAULT_AUTOPLAY_CONFIG: Required<AutoplayConfig> = {
  delay: 4000,
  stopOnInteraction: true,
  stopOnMouseEnter: true,
  stopOnFocusIn: true,
  playOnInit: true,
};

/**
 * Custom autoplay hook for Embla Carousel
 * Provides more control than the embla-carousel-autoplay plugin
 */
export function useAutoplay(
  api: EmblaCarouselType | undefined,
  config: AutoplayConfig = {}
) {
  const options = { ...DEFAULT_AUTOPLAY_CONFIG, ...config };
  const [isPlaying, setIsPlaying] = useState(options.playOnInit);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const play = useCallback(() => {
    if (!api) return;

    stop();
    setIsPlaying(true);

    intervalRef.current = setInterval(() => {
      if (!api.canScrollNext()) {
        api.scrollTo(0);
      } else {
        api.scrollNext();
      }
    }, options.delay);
  }, [api, options.delay, stop]);

  const toggle = useCallback(() => {
    if (isPlaying) {
      stop();
    } else {
      play();
    }
  }, [isPlaying, play, stop]);

  // Auto-start on init
  useEffect(() => {
    if (!api || !options.playOnInit) return;

    // Small delay to ensure carousel is ready
    const timeoutId = setTimeout(play, 100);
    return () => clearTimeout(timeoutId);
  }, [api, options.playOnInit, play]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stop();
  }, [stop]);

  // Handle user interactions
  useEffect(() => {
    if (!api) return;

    const handleInteraction = () => {
      if (options.stopOnInteraction && isPlaying) {
        stop();
      }
    };

    api.on("pointerDown", handleInteraction);

    return () => {
      api.off("pointerDown", handleInteraction);
    };
  }, [api, options.stopOnInteraction, isPlaying, stop]);

  return {
    isPlaying,
    play,
    stop,
    toggle,
  };
}

/**
 * Hook for continuous/infinite scrolling (like origin section)
 * Creates a smooth, non-stopping scroll effect
 */
export function useContinuousScroll(
  api: EmblaCarouselType | undefined,
  speed: number = 1
) {
  const animationRef = useRef<number | null>(null);
  const [isScrolling, setIsScrolling] = useState(true);

  const stopScroll = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    setIsScrolling(false);
  }, []);

  const startScroll = useCallback(() => {
    if (!api) return;

    setIsScrolling(true);

    const scroll = () => {
      if (!api) return;
      const engine = api.internalEngine();
      engine.location.add(-speed);
      engine.target.set(engine.location.get());
      engine.scrollLooper.loop(-1);
      engine.slideLooper.loop();
      engine.translate.to(engine.location.get());

      animationRef.current = requestAnimationFrame(scroll);
    };

    animationRef.current = requestAnimationFrame(scroll);
  }, [api, speed]);

  useEffect(() => {
    if (!api) return;
    startScroll();
    return () => stopScroll();
  }, [api, startScroll, stopScroll]);

  return {
    isScrolling,
    startScroll,
    stopScroll,
  };
}
